import React, { useEffect, useRef, useState } from "react";
import { Star, Phone, MapPin, Plus, Trash2, Loader2, Repeat, Car, Store, User, Check, Bike, FileCheck, ShieldCheck, AlertTriangle, CheckCircle2, Circle, Home, Briefcase, Snowflake } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { PageHeader, Section, StatusBadge } from "../../components/app/ui";
import { OtpBoxes } from "../../components/auth/OtpBoxes";
import { ResendCountdown } from "../../components/auth/ResendCountdown";
import { formatIndiaPhoneInput, indiaPhoneDigits, isValidIndiaPhone } from "../../lib/smartDefaults";
import RestaurantProfileForm from "../../components/app/RestaurantProfileForm";
import MyReports from "../../components/app/MyReports";
import CancellationFeesCard from "../../components/app/CancellationFeesCard";
import RefundHistoryCard from "../../components/app/RefundHistoryCard";
import ReliabilityCard from "../../components/app/ReliabilityCard";
import KycStatus from "./KycStatus";
import ImageField from "../../components/app/ImageField";
import { useAuth } from "../../context/AuthContext";
import { authApi, addressApi, driverApi, restaurantApi, privateCarApi, blockApi, errorMessage, formatDate, titleCase, roleLabel } from "../../lib/api";
import AddressAutocomplete from "../../components/app/AddressAutocomplete";
import PlaceInput from "../../components/app/PlaceInput";
import NotificationPrefs from "../../components/app/NotificationPrefs";
import ProfileReminder from "../../components/app/ProfileReminder";
import { useNavigate, useSearchParams } from "react-router-dom";
import { WhatsAppChatButton, WhatsAppOptIn } from "../../components/app/WhatsApp";
import { homeForRole } from "../../context/AuthContext";
import { categoryOf, VEHICLE_CATEGORIES, isPrivateCarCategory, saveDriverLocal } from "../../lib/driver";
import { useUnsavedChanges } from "../../hooks/useUnsavedChanges";

const ROLES = [{ value: "rider", label: "Customer", icon: User }, { value: "driver", label: "Driver", icon: Car }, { value: "restaurant", label: "Restaurant", icon: Store }];

// "What you can do next" shown right after switching into a role.
const ROLE_HINTS = {
  rider: { title: "You're back to Customer", icon: User, items: ["Book taxis and scooters in seconds", "Order food from local restaurants", "Track your rides and orders live"] },
  driver: { title: "You're now a Driver", icon: Car, items: ["Go online to receive taxi & delivery requests", "Complete KYC to start accepting trips", "Track your earnings and weekly payouts"] },
  restaurant: { title: "You're now a Restaurant", icon: Store, items: ["Set up your menu, hours & photos", "Complete KYC to go live and accept orders", "Manage orders, discounts and payouts"] },
};

// Driver and Restaurant need KYC; Customer never does.
export function roleNeedsKyc(user, role) {
  if (role === "rider" || role === "customer") return false;
  if (user?.[`${role}_verified`] === true) return false;
  const status = user?.verifications?.[role] || (user?.role === role ? user?.verification_status : null);
  return !(status === "approved" || status === "verified");
}

// Phone is a VERIFIED, mandatory field. Adding or changing it requires a WhatsApp OTP.
function PhoneField({ user, onSaved }) {
  const verified = !!user.phone_verified && !!user.phone;
  const [phone, setPhone] = useState(formatIndiaPhoneInput(user.phone || ""));
  const [stage, setStage] = useState("idle"); // idle | code
  const [otp, setOtp] = useState("");
  const [reqId, setReqId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(!verified);
  const dirty = editing && indiaPhoneDigits(phone) !== indiaPhoneDigits(user.phone || "");
  useUnsavedChanges(dirty);

  const sendCode = async () => {
    if (!isValidIndiaPhone(phone)) return toast.error("Enter a valid 10-digit mobile number (no leading 0)");
    setBusy(true);
    try {
      const value = `+91${indiaPhoneDigits(phone)}`;
      const r = await authApi.otpRequest({ channel: "whatsapp", phone: value });
      setReqId(r.request_id);
      setStage("code");
      toast.success(`We sent a WhatsApp code to ${value}`);
    } catch (e) {
      toast.error(errorMessage(e, "Couldn't send the code — WhatsApp verification may be unavailable right now."));
    } finally { setBusy(false); }
  };
  const verify = async () => {
    if (otp.trim().length < 4) return toast.error("Enter the 6-digit code");
    setBusy(true);
    try {
      const value = `+91${indiaPhoneDigits(phone)}`;
      const updated = await authApi.linkPhone({ phone: value, otp: otp.trim(), request_id: reqId });
      onSaved(updated.user || updated);
      setStage("idle"); setOtp(""); setReqId(null); setEditing(false);
      toast.success("Phone verified — WhatsApp updates are on");
    } catch (e) {
      toast.error(errorMessage(e, "Invalid or expired code"));
    } finally { setBusy(false); }
  };

  const autoVerifyRef = useRef("");
  useEffect(() => {
    if (stage === "code" && otp.length === 6 && !busy && autoVerifyRef.current !== otp) { autoVerifyRef.current = otp; verify(); }
  }, [otp, stage, busy]); // eslint-disable-line react-hooks/exhaustive-deps

  if (verified && !editing) {
    return (
      <div className="space-y-2" data-testid="phone-field">
        <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5">
          <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
          <span className="flex-1 text-sm font-medium text-ink" data-testid="phone-field-verified">{user.phone}</span>
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">Verified</span>
          <Button type="button" variant="ghost" size="sm" className="h-7 rounded-full text-xs" onClick={() => { setEditing(true); setStage("idle"); setPhone(formatIndiaPhoneInput(user.phone || "")); }} data-testid="phone-field-change">Change</Button>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="phone-field">
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input value={phone} onChange={(e) => setPhone(formatIndiaPhoneInput(e.target.value))} placeholder="+91 98765 43210" className="h-10 flex-1" disabled={stage === "code"} data-testid="phone-field-input" />
        {stage === "idle" ? (
          <Button type="button" onClick={sendCode} disabled={busy} className="h-10 rounded-xl bg-brand text-white hover:bg-brand-600" data-testid="phone-field-send">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Phone className="mr-2 h-4 w-4" />} Send code</Button>
        ) : (
          <Button type="button" variant="ghost" onClick={() => { setStage("idle"); setOtp(""); }} className="h-10 rounded-xl" data-testid="phone-field-editnum">Edit number</Button>
        )}
      </div>
      {stage === "code" && (
        <div className="space-y-3">
          <div className="flex flex-col items-center gap-3">
            <OtpBoxes value={otp} onChange={(v) => setOtp(v)} autoFocus testId="phone-field-otp" />
            <Button type="button" onClick={verify} disabled={busy || otp.length < 4} className="h-11 w-full rounded-xl bg-brand text-white hover:bg-brand-600" data-testid="phone-field-verify">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />} Verify</Button>
          </div>
          <ResendCountdown onResend={sendCode} busy={busy} nonce={reqId} testId="phone-field-resend" />
        </div>
      )}
      <p className="text-xs text-ink-muted" data-testid="phone-field-hint">We'll verify your number with a WhatsApp code. Once verified, WhatsApp updates (ride &amp; order alerts) turn on automatically — you can switch them off anytime below.</p>
    </div>
  );
}

function Addresses() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ label: "Home", address: "", lat: null, lng: null, house_no: "", floor: "", landmark: "", directions: "" });
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const LABELS = ["Home", "Work", "Friend", "Other"];
  const detailLine = (a) => [a.house_no, a.floor && `Floor ${a.floor}`, a.landmark].filter(Boolean).join(" · ");
  const load = () => addressApi.list().then(setList).catch(() => {});
  useEffect(() => { load(); }, []);
  const resetForm = () => { setForm({ label: "Home", address: "", lat: null, lng: null, house_no: "", floor: "", landmark: "", directions: "" }); setEditId(null); };
  const submit = async (e) => {
    e.preventDefault();
    if (!form.address.trim()) return toast.error("Enter an address");
    setBusy(true);
    try {
      const body = { label: form.label, address: form.address, lat: form.lat ?? 25.6751, lng: form.lng ?? 94.1086, house_no: form.house_no.trim(), floor: form.floor.trim(), landmark: form.landmark.trim(), directions: form.directions.trim() };
      if (editId) { await addressApi.patch(editId, body); toast.success("Address updated"); }
      else { await addressApi.add({ ...body, is_default: list.length === 0 }); toast.success("Address saved"); }
      resetForm(); load();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  const edit = (a) => { setEditId(a.address_id || a.id); setForm({ label: LABELS.includes(a.label) ? a.label : "Other", address: a.address, lat: a.lat, lng: a.lng, house_no: a.house_no || "", floor: a.floor || "", landmark: a.landmark || "", directions: a.directions || "" }); };
  const remove = async (a) => { try { await addressApi.remove(a.address_id || a.id); if (editId === (a.address_id || a.id)) resetForm(); load(); } catch (err) { toast.error(errorMessage(err)); } };
  const makeDefault = async (a) => { try { await addressApi.patch(a.address_id || a.id, { is_default: true }); load(); } catch (err) { toast.error(errorMessage(err)); } };
  const setF = (k) => (e) => { const v = e.target.value; setForm((f) => ({ ...f, [k]: v })); };
  const labelIcon = (l) => /home/i.test(l) ? Home : /work|office/i.test(l) ? Briefcase : /friend/i.test(l) ? User : MapPin;
  return (
    <div className="space-y-4" data-testid="addresses">
      <ul className="space-y-2">
        {list.length === 0 && <li className="text-sm text-ink-muted">No saved addresses yet. Add Home, Work, a Friend's place or any spot you order to often.</li>}
        {list.map((a) => {
          const LIcon = labelIcon(a.label);
          const details = detailLine(a);
          return (
            <li key={a.address_id || a.id} className="flex items-start gap-3 rounded-xl bg-surface-100 px-4 py-3" data-testid={`address-row-${a.address_id || a.id}`}>
              <LIcon className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-ink">{a.label} {a.is_default && <span className="ml-1 rounded-full bg-graphite px-2 py-0.5 text-[10px] font-semibold uppercase text-white">default</span>}</p>
                <p className="truncate text-xs text-ink-soft">{a.address}</p>
                {details && <p className="mt-0.5 truncate text-xs text-ink-muted" data-testid={`address-details-${a.address_id || a.id}`}>{details}</p>}
                {a.directions && <p className="mt-0.5 truncate text-xs italic text-ink-muted">“{a.directions}”</p>}
              </div>
              {!a.is_default && <button onClick={() => makeDefault(a)} className="text-xs font-medium text-brand hover:underline" data-testid={`address-default-${a.address_id || a.id}`}>Set default</button>}
              <button onClick={() => edit(a)} className="text-xs font-medium text-ink-soft hover:text-brand hover:underline" data-testid={`address-edit-${a.address_id || a.id}`}>Edit</button>
              <button onClick={() => remove(a)} className="rounded-lg p-1.5 text-ink-muted hover:bg-white hover:text-red-700" aria-label="Delete" data-testid={`address-delete-${a.address_id || a.id}`}><Trash2 className="h-4 w-4" /></button>
            </li>
          );
        })}
      </ul>
      <form onSubmit={submit} className="space-y-3 rounded-xl border border-surface-300 bg-white p-3" data-testid="address-form">
        <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">{editId ? "Edit address" : "Add a new address"}</p>
        <div className="flex flex-wrap gap-2" data-testid="address-label-chips">
          {LABELS.map((l) => {
            const LIcon = labelIcon(l);
            return (
              <button key={l} type="button" onClick={() => setForm((f) => ({ ...f, label: l }))} className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold transition-colors ${form.label === l ? "bg-brand text-white" : "bg-surface-100 text-ink-soft hover:text-ink"}`} data-testid={`address-label-${l.toLowerCase()}`}><LIcon className="h-3.5 w-3.5" />{l}</button>
            );
          })}
        </div>
        <AddressAutocomplete value={form.address} onTextChange={(t) => setForm((f) => ({ ...f, address: t, lat: null, lng: null }))} onSelect={({ address, lat, lng }) => setForm((f) => ({ ...f, address, lat, lng }))} placeholder="Search your full address" testId="address-text" />
        <div className="grid grid-cols-2 gap-2">
          <Input value={form.house_no} onChange={setF("house_no")} placeholder="House / Flat no." className="h-10" data-testid="address-house" />
          <Input value={form.floor} onChange={setF("floor")} placeholder="Floor" className="h-10" data-testid="address-floor" />
        </div>
        <Input value={form.landmark} onChange={setF("landmark")} placeholder="Landmark (e.g. near State Bank)" className="h-10" data-testid="address-landmark" />
        <textarea value={form.directions} onChange={setF("directions")} rows={2} placeholder="Directions for driver (e.g. blue gate, ring the top bell)" className="w-full rounded-md border border-surface-300 bg-white p-2 text-sm" data-testid="address-directions" />
        <div className="flex gap-2">
          <Button type="submit" disabled={busy} className="h-10 rounded-xl bg-brand text-white hover:bg-brand-600" data-testid="address-add">{busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />} {editId ? "Save changes" : "Add address"}</Button>
          {editId && <Button type="button" variant="outline" onClick={resetForm} className="h-10 rounded-xl" data-testid="address-cancel-edit">Cancel</Button>}
        </div>
      </form>
    </div>
  );
}

function DriverReadiness() {
  const [data, setData] = useState(null);
  useEffect(() => { driverApi.readiness().then(setData).catch(() => setData(null)); }, []);
  if (!data || data.complete) return null;
  return (
    <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4" data-testid="driver-readiness-banner">
      <p className="flex items-center gap-2 text-sm font-semibold text-amber-900"><AlertTriangle className="h-4 w-4 text-amber-600" /> Finish your profile to start accepting work</p>
      <p className="mt-1 text-xs text-amber-800">{data.reason}</p>
      <ul className="mt-3 space-y-1">
        {(data.items || []).map((it) => (
          <li key={it.key} className="flex items-center gap-2 text-sm" data-testid={`readiness-${it.key}`}>
            {it.done ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <Circle className="h-4 w-4 text-amber-500" />}
            <span className={it.done ? "text-ink-soft line-through" : "font-medium text-ink"}>{it.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function DriverServiceArea({ user, onSaved }) {
  const [busy, setBusy] = useState(false);
  const current = user?.taxi_scope;
  if (current == null) return null;
  const opts = [
    { value: "local", label: "Local", desc: "Within-city only" },
    { value: "regional", label: "Regional", desc: "Intercity only" },
    { value: "both", label: "Both", desc: "City + intercity" },
  ];
  const change = async (scope) => {
    if (scope === current || busy) return;
    setBusy(true);
    try { const res = await driverApi.setTaxiScope(scope); onSaved(res.user || { ...user, taxi_scope: scope }); toast.success("Service area updated"); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="driver-service-area">
      {opts.map((o) => {
        const on = current === o.value;
        return (
          <button type="button" key={o.value} disabled={busy} onClick={() => change(o.value)} data-testid={`service-area-${o.value}`}
            className={`rounded-xl border p-3 text-left transition-colors ${on ? "border-sky-500 bg-sky-50 ring-1 ring-sky-400" : "border-surface-300 bg-white hover:border-sky-200"}`}>
            <p className="text-sm font-semibold text-ink">{o.label}</p>
            <p className="text-[11px] leading-tight text-ink-muted">{o.desc}</p>
          </button>
        );
      })}
    </div>
  );
}

function ProfileProgress() {
  const [data, setData] = useState(null);
  useEffect(() => { authApi.profileProgress().then(setData).catch(() => setData(null)); }, []);
  if (!data || !data.total) return null;
  const pct = Math.round((data.done / data.total) * 100);
  const allDone = data.done >= data.total;
  return (
    <div className="kt-card p-5" data-testid="profile-progress">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-ink">{allDone ? "Your profile is complete 🎉" : "Finish setting up your profile"}</p>
        <span className="text-sm font-bold text-brand" data-testid="profile-progress-count">{data.done} of {data.total} done</span>
      </div>
      <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-surface-200">
        <div className={`h-full rounded-full ${allDone ? "bg-green-500" : "bg-brand"}`} style={{ width: `${pct}%` }} data-testid="profile-progress-bar" />
      </div>
      {!allDone && (
        <ul className="mt-3 grid grid-cols-1 gap-1 sm:grid-cols-2">
          {(data.items || []).map((it) => (
            <li key={it.key} className="flex items-center gap-2 text-xs" data-testid={`progress-${it.key}`}>
              {it.done ? <CheckCircle2 className="h-3.5 w-3.5 text-green-600" /> : <Circle className="h-3.5 w-3.5 text-ink-muted" />}
              <span className={it.done ? "text-ink-muted line-through" : "font-medium text-ink"}>{it.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function DriverVehicle({ user, onSaved }) {
  const navigate = useNavigate();
  const [category, setCategory] = useState(categoryOf(user) || "private_scooter");
  const [carModel, setCarModel] = useState(user.car_model || "");
  const [carMake, setCarMake] = useState(user.car_make || "");
  const [carYear, setCarYear] = useState(user.car_year ? String(user.car_year) : "");
  const [vehiclePlate, setVehiclePlate] = useState(user.vehicle_plate || "");
  const [facilities, setFacilities] = useState(user.facilities || []);
  const [otherFacilities, setOtherFacilities] = useState(user.other_facilities || "");
  const [hasAc, setHasAc] = useState(user.has_ac == null ? null : !!user.has_ac);
  const [base, setBase] = useState(user.base_lat ? { lat: user.base_lat, lng: user.base_lng, name: user.base_address } : null);
  const [dailyRate, setDailyRate] = useState(user.private_daily_rate ? String(user.private_daily_rate) : "");
  const [photos, setPhotos] = useState(user.car_photos || []);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const selected = VEHICLE_CATEGORIES.find((c) => c.value === category);
  const isPrivateCar = isPrivateCarCategory(category);
  const isTaxiCar = !!selected && (selected.vehicle === "car" || selected.vehicle === "jumbo");
  const dirty = category !== (categoryOf(user) || "private_scooter")
    || carMake !== (user.car_make || "") || carModel !== (user.car_model || "")
    || carYear !== (user.car_year ? String(user.car_year) : "")
    || vehiclePlate !== (user.vehicle_plate || "")
    || dailyRate !== (user.private_daily_rate ? String(user.private_daily_rate) : "")
    || (isTaxiCar && hasAc !== (user.has_ac == null ? null : !!user.has_ac))
    || JSON.stringify(facilities) !== JSON.stringify(user.facilities || [])
    || otherFacilities !== (user.other_facilities || "");
  useUnsavedChanges(dirty);
  const FACILITIES = ["AC", "Sunroof", "Sound System", "Fog lights", "GPS", "Luggage carrier"];
  const toggleFac = (f) => setFacilities((p) => (p.includes(f) ? p.filter((x) => x !== f) : [...p, f]));

  const addPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (photos.length >= 5) { toast.error("You can upload at most 5 photos."); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Image must be under 10 MB."); return; }
    setPhotoBusy(true);
    try {
      const file_data = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(String(r.result).split(",")[1]);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      const out = await privateCarApi.uploadPhoto({ file_data, mime: file.type, file_name: file.name });
      setPhotos(out.photos || []);
      toast.success("Photo added");
    } catch (err) { toast.error(errorMessage(err, "Could not upload photo")); }
    finally { setPhotoBusy(false); }
  };

  const removePhoto = async (pid) => {
    try { const out = await privateCarApi.deletePhoto(pid); setPhotos(out.photos || []); }
    catch (err) { toast.error(errorMessage(err, "Could not remove photo")); }
  };

  const save = async () => {
    if (isPrivateCar && (!carModel.trim() || !carMake.trim() || !(Number(dailyRate) > 0) || !base)) {
      toast.error("Enter your car make, model, daily rate and base location.");
      return;
    }
    if (isTaxiCar && hasAc == null) {
      toast.error("Please tell us whether your car has Air Conditioning.");
      return;
    }
    const payload = { vehicle_category: category };
    payload.vehicle_plate = vehiclePlate.trim();
    if (isPrivateCar) {
      payload.car_make = carMake.trim();
      payload.car_model = carModel.trim();
      payload.car_year = carYear ? Number(carYear) : null;
      payload.facilities = facilities;
      payload.other_facilities = otherFacilities.trim();
      payload.private_daily_rate = Number(dailyRate);
      payload.base_lat = base.lat; payload.base_lng = base.lng; payload.base_address = base.name;
    } else {
      payload.car_make = carMake.trim();
      payload.car_model = carModel.trim();
      payload.car_year = carYear ? Number(carYear) : null;
      if (isTaxiCar) payload.has_ac = !!hasAc;
    }
    setBusy(true);
    let r = null;
    try {
      r = await driverApi.profile(payload);
    } catch {
      /* local-first — backend may be older */
    }
    saveDriverLocal(payload);
    onSaved({ ...(r?.user || r || user), ...payload, vehicle_category: category });
    setBusy(false);
    toast.success("Saved — your driver profile is updated");
    if (selected?.commercial) {
      toast.info("Upload your Commercial Taxi Permit in KYC to get approved.");
    }
  };

  return (
    <div className="space-y-5" data-testid="driver-vehicle">
      <div className="space-y-2">
        <Label>What vehicle do you drive?</Label>
        <p className="text-xs text-ink-soft">This decides whether you receive food orders, customer rides, or both.</p>
        <div className="grid gap-2 sm:grid-cols-2">
          {VEHICLE_CATEGORIES.map((c) => {
            const active = category === c.value;
            return (
              <button type="button" key={c.value} onClick={() => setCategory(c.value)} data-testid={`vehicle-category-${c.value}`}
                className={`flex items-start gap-3 rounded-2xl border p-4 text-left transition-colors ${active ? "border-brand bg-brand-50" : "border-surface-300 hover:border-brand-200"}`}>
                <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${active ? "bg-brand text-white" : "bg-surface-100 text-ink-muted"}`}>
                  {c.vehicle === "scooter" ? <Bike className="h-5 w-5" /> : <Car className="h-5 w-5" />}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-ink">{c.label}</span>
                  <span className="mt-0.5 inline-flex rounded-full bg-graphite px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">{c.role}</span>
                  <span className="mt-1 block text-xs text-ink-soft">{c.desc}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {!isPrivateCar && (
        <div className="grid gap-4 rounded-2xl border border-surface-300 bg-surface-50 p-4 sm:grid-cols-2" data-testid="vehicle-details-fields">
          <div className="sm:col-span-2 flex items-center gap-2 text-sm font-semibold text-ink"><Car className="h-4 w-4 text-brand" /> Vehicle details</div>
          <div className="space-y-2 sm:col-span-2"><Label>Vehicle number (plate)</Label><Input value={vehiclePlate} onChange={(e) => setVehiclePlate(e.target.value.toUpperCase())} placeholder="e.g. NL01 AB 1234" className="h-10" data-testid="vehicle-plate" /></div>
          <div className="space-y-2"><Label>Make / manufacturer</Label><Input value={carMake} onChange={(e) => setCarMake(e.target.value)} placeholder="e.g. Honda" className="h-10" data-testid="ride-car-make" /></div>
          <div className="space-y-2"><Label>Model</Label><Input value={carModel} onChange={(e) => setCarModel(e.target.value)} placeholder="e.g. Activa" className="h-10" data-testid="ride-car-model" /></div>
          <div className="space-y-2 sm:col-span-2"><Label>Car photos <span className="font-normal text-ink-muted">(up to 5 — customers see these)</span></Label>
            <div className="flex flex-wrap gap-2" data-testid="car-photos">
              {photos.map((pid) => (
                <div key={pid} className="relative h-20 w-28 overflow-hidden rounded-xl border border-surface-300">
                  <img src={privateCarApi.photoUrl(pid)} alt="car" className="h-full w-full object-cover" />
                  <button type="button" onClick={() => removePhoto(pid)} className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white hover:bg-red-600" data-testid={`car-photo-del-${pid}`} aria-label="Remove photo"><Trash2 className="h-3 w-3" /></button>
                </div>
              ))}
              {photos.length < 5 && (
                <label className="flex h-20 w-28 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-surface-300 text-xs text-ink-soft hover:border-brand hover:text-brand" data-testid="car-photo-add">
                  {photoBusy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Plus className="h-5 w-5" />}
                  <span>Add photo</span>
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={addPhoto} disabled={photoBusy} />
                </label>
              )}
            </div>
            <p className="text-xs text-ink-muted">Riders see your actual vehicle on the map and nearby list. Save your profile first, then add photos.</p>
          </div>
          <p className="sm:col-span-2 text-xs text-ink-muted">Your vehicle number and make/model help us verify your vehicle and show it to customers.</p>
          {isTaxiCar && (
            <div className="sm:col-span-2 space-y-2 rounded-xl border border-sky-200 bg-sky-50/60 p-3" data-testid="driver-ac-field">
              <Label className="flex items-center gap-1.5"><Snowflake className="h-4 w-4 text-sky-500" /> Does your car have Air Conditioning?</Label>
              <p className="text-xs text-ink-soft">AC rides pay a 20% add-on and are offered to AC cars first. Only select Yes if your car's AC works.</p>
              <div className="flex gap-2">
                <button type="button" onClick={() => setHasAc(true)} className={`flex-1 rounded-full px-3 py-2 text-sm font-semibold transition-colors ${hasAc === true ? "bg-sky-600 text-white" : "bg-surface-100 text-ink-soft"}`} data-testid="driver-ac-yes">Yes, it has AC</button>
                <button type="button" onClick={() => setHasAc(false)} className={`flex-1 rounded-full px-3 py-2 text-sm font-semibold transition-colors ${hasAc === false ? "bg-graphite text-white" : "bg-surface-100 text-ink-soft"}`} data-testid="driver-ac-no">No AC</button>
              </div>
            </div>
          )}
        </div>
      )}

      {isPrivateCar && (
        <div className="grid gap-4 rounded-2xl border border-surface-300 bg-surface-50 p-4 sm:grid-cols-2" data-testid="private-car-fields">
          <div className="sm:col-span-2 flex items-center gap-2 text-sm font-semibold text-ink"><Car className="h-4 w-4 text-brand" /> Your private car</div>
          <div className="space-y-2 sm:col-span-2"><Label>Vehicle number (plate)</Label><Input value={vehiclePlate} onChange={(e) => setVehiclePlate(e.target.value.toUpperCase())} placeholder="e.g. NL01 AB 1234" className="h-10" data-testid="vehicle-plate" /></div>
          <div className="space-y-2"><Label>Make / manufacturer</Label><Input value={carMake} onChange={(e) => setCarMake(e.target.value)} placeholder="e.g. Toyota" className="h-10" data-testid="car-make" /></div>
          <div className="space-y-2"><Label>Model</Label><Input value={carModel} onChange={(e) => setCarModel(e.target.value)} placeholder="e.g. Innova Crysta" className="h-10" data-testid="car-model" /></div>
          <div className="space-y-2"><Label>Year</Label><Input type="number" value={carYear} onChange={(e) => setCarYear(e.target.value)} placeholder="e.g. 2022" className="h-10" data-testid="car-year" /></div>
          <div className="space-y-2"><Label>Daily rate (₹ / day)</Label><Input type="number" min="0" value={dailyRate} onChange={(e) => setDailyRate(e.target.value)} placeholder="e.g. 3500" className="h-10" data-testid="daily-rate" /></div>
          <div className="space-y-2 sm:col-span-2"><Label>Facilities</Label>
            <div className="flex flex-wrap gap-2" data-testid="facilities">
              {FACILITIES.map((f) => (
                <button type="button" key={f} onClick={() => toggleFac(f)} className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${facilities.includes(f) ? "bg-graphite text-white" : "bg-surface-100 text-ink-soft"}`} data-testid={`fac-${f.replace(/\s+/g, "-").toLowerCase()}`}>{f}</button>
              ))}
            </div>
          </div>
          <div className="space-y-2 sm:col-span-2"><Label>Other facilities you provide <span className="font-normal text-ink-muted">(optional)</span></Label>
            <textarea value={otherFacilities} onChange={(e) => setOtherFacilities(e.target.value)} rows={2} maxLength={500} placeholder="e.g. bottled water, phone charger, child seat, English-speaking, help with luggage" className="w-full rounded-xl border border-surface-300 bg-white px-3 py-2 text-sm text-ink placeholder:text-ink-muted" data-testid="other-facilities" />
          </div>
          <div className="space-y-2 sm:col-span-2"><Label>Base location (where your car is based)</Label>
            <PlaceInput value={base} onChange={setBase} placeholder="Search your area / town" allowCurrent testId="base-location" />
          </div>
          <div className="space-y-2 sm:col-span-2"><Label>Car photos <span className="font-normal text-ink-muted">(up to 5 — customers see these)</span></Label>
            <div className="flex flex-wrap gap-2" data-testid="car-photos">
              {photos.map((pid) => (
                <div key={pid} className="relative h-20 w-28 overflow-hidden rounded-xl border border-surface-300">
                  <img src={privateCarApi.photoUrl(pid)} alt="car" className="h-full w-full object-cover" />
                  <button type="button" onClick={() => removePhoto(pid)} className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white hover:bg-red-600" data-testid={`car-photo-del-${pid}`} aria-label="Remove photo"><Trash2 className="h-3 w-3" /></button>
                </div>
              ))}
              {photos.length < 5 && (
                <label className="flex h-20 w-28 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-surface-300 text-xs text-ink-soft hover:border-brand hover:text-brand" data-testid="car-photo-add">
                  {photoBusy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Plus className="h-5 w-5" />}
                  <span>Add photo</span>
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={addPhoto} disabled={photoBusy} />
                </label>
              )}
            </div>
          </div>
          <p className="sm:col-span-2 text-xs text-ink-muted">You keep the full daily fare, paid directly by the customer. Fuel and toll are also paid by the customer directly to you (actual trip costs). The customer pays KohimaTaxi a flat ₹300 + 5% GST (₹315) platform fee upfront to send a booking request.</p>
        </div>
      )}

      {selected?.commercial && (
        <div className="flex items-start gap-2 rounded-2xl border border-surface-300 bg-surface-50 p-4 text-xs text-ink-soft" data-testid="permit-kyc-note">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
          <p>Commercial vehicles need a valid Commercial Taxi Permit. Add your permit number, expiry and photo in <button type="button" onClick={() => navigate("/app/kyc")} className="font-semibold text-brand hover:underline" data-testid="permit-kyc-link">KYC</button> to get approved for rides.</p>
        </div>
      )}

      <Button onClick={save} disabled={busy} className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid="vehicle-save">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save driver profile</Button>
    </div>
  );
}

function BlockedUsers() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");
  const load = async () => { try { setRows(await blockApi.list()); } catch { setRows([]); } };
  useEffect(() => { load(); }, []);
  const unblock = async (id) => {
    setBusy(id);
    try { await blockApi.unblock(id); toast.success("Unblocked"); await load(); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(""); }
  };
  if (rows === null) return <p className="text-sm text-ink-muted" data-testid="blocked-loading">Loading…</p>;
  if (rows.length === 0) return <p className="text-sm text-ink-muted" data-testid="blocked-empty">You haven't blocked anyone.</p>;
  return (
    <ul className="space-y-2" data-testid="blocked-list">
      {rows.map((b) => (
        <li key={b.blocked_id} className="flex items-center justify-between rounded-xl border border-surface-200 px-4 py-3" data-testid={`blocked-row-${b.blocked_id}`}>
          <div className="min-w-0"><p className="truncate text-sm font-semibold text-ink">{b.name || "User"}</p>{b.role && <p className="text-xs text-ink-muted">{titleCase(b.role)}</p>}</div>
          <Button onClick={() => unblock(b.blocked_id)} disabled={busy === b.blocked_id} variant="outline" className="rounded-full" data-testid={`blocked-unblock-${b.blocked_id}`}>{busy === b.blocked_id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Unblock"}</Button>
        </li>
      ))}
    </ul>
  );
}

function DeleteAccount() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await authApi.deleteAccount();
      toast.success("Your account and personal data have been deleted.");
      logout();
      navigate("/");
    } catch (e) {
      toast.error(errorMessage(e));
      setBusy(false);
    }
  };
  return (
    <div className="rounded-2xl border border-red-200 bg-red-50/50 p-5" data-testid="delete-account">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-red-700"><Trash2 className="h-4 w-4" /> Delete account</h3>
      <p className="mt-1.5 text-xs text-red-700/80">Permanently deletes your account and personal data (profile, verification documents, scheduled rides). This can't be undone. Past ride/order records are retained as required by law.</p>
      {!open ? (
        <button onClick={() => setOpen(true)} className="mt-3 rounded-full border border-red-300 bg-white px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-100" data-testid="delete-account-open">Delete my account</button>
      ) : (
        <div className="mt-3 space-y-2">
          <p className="text-xs font-medium text-red-700">Type <span className="font-bold">DELETE</span> to confirm:</p>
          <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="DELETE" className="h-10 max-w-xs border-red-300" data-testid="delete-account-confirm-input" />
          <div className="flex gap-2">
            <Button onClick={run} disabled={busy || confirm !== "DELETE"} className="rounded-full bg-red-600 text-white hover:bg-red-700" data-testid="delete-account-confirm">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Permanently delete</Button>
            <Button onClick={() => { setOpen(false); setConfirm(""); }} variant="ghost" className="rounded-full" data-testid="delete-account-cancel">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function AccountSettings({ user, onSaved }) {
  const isPhoneEmail = user.email?.endsWith("@phone.local");
  const currentEmail = isPhoneEmail ? "" : user.email || "";
  const [name, setName] = useState(user.name || "");
  const [photo, setPhoto] = useState(user.photo || "");
  const [vehiclePhoto, setVehiclePhoto] = useState(user.vehicle_photo || "");
  const [savingP, setSavingP] = useState(false);
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [savingPw, setSavingPw] = useState(false);
  const hasPassword = user.has_password;
  // Email change (OTP verified)
  const [newEmail, setNewEmail] = useState("");
  const [emailStage, setEmailStage] = useState("idle"); // idle | code
  const [emailOtp, setEmailOtp] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const profileDirty = name !== (user.name || "") || (photo || "") !== (user.photo || "") || (vehiclePhoto || "") !== (user.vehicle_photo || "");
  useUnsavedChanges(profileDirty);

  const saveProfile = async () => {
    setSavingP(true);
    try {
      const updated = await authApi.updateProfile({ name, photo: photo || null, vehicle_photo: vehiclePhoto || null });
      onSaved(updated);
      toast.success("Profile updated");
    } catch (e) { toast.error(errorMessage(e)); } finally { setSavingP(false); }
  };
  const requestEmail = async () => {
    if (!newEmail.trim() || !newEmail.includes("@")) return toast.error("Enter a valid email");
    setEmailBusy(true);
    try {
      const r = await authApi.emailChangeRequest(newEmail.trim());
      setEmailStage("code");
      toast.success(`We sent a code to ${r.email_masked || newEmail.trim()}`);
      if (r.dev_mock && r.demo_otp) toast.info(`Test mode code: ${r.demo_otp}`);
    } catch (e) { toast.error(errorMessage(e)); } finally { setEmailBusy(false); }
  };
  const verifyEmail = async () => {
    if (emailOtp.trim().length < 4) return toast.error("Enter the 6-digit code");
    setEmailBusy(true);
    try {
      const updated = await authApi.emailChangeVerify(emailOtp.trim());
      onSaved(updated);
      setEmailStage("idle"); setNewEmail(""); setEmailOtp("");
      toast.success("Email address updated");
    } catch (e) { toast.error(errorMessage(e)); } finally { setEmailBusy(false); }
  };
  const savePassword = async () => {
    if (nw.length < 6) return toast.error("New password must be at least 6 characters");
    setSavingPw(true);
    try {
      await authApi.changePassword({ current_password: cur || null, new_password: nw });
      setCur(""); setNw("");
      toast.success("Password changed");
    } catch (e) { toast.error(errorMessage(e)); } finally { setSavingPw(false); }
  };

  return (
    <>
      <Section title="Edit profile" description="Update your name and photo.">
        <div className="space-y-4">
          <div className="space-y-2"><Label>Profile photo</Label><ImageField value={photo} onChange={setPhoto} testId="profile-photo-field" /></div>
          {user.role === "driver" && (
            <div className="space-y-2" data-testid="vehicle-photo-field"><Label>Vehicle photo</Label><ImageField value={vehiclePhoto} onChange={setVehiclePhoto} testId="vehicle-photo" /><p className="text-xs text-ink-muted">Customers see this photo of your vehicle once you accept their ride, so they can spot you easily.</p></div>
          )}
          <div className="space-y-2"><Label>Full name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name" /></div>
          <Button onClick={saveProfile} disabled={savingP || !profileDirty} className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid="profile-save">{savingP && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save profile</Button>
        </div>
      </Section>
      <Section title={currentEmail ? "Change email" : "Add an email"} description={currentEmail ? "We'll send a code to the new address to confirm it's yours before switching." : "Add an email and verify it with a one-time code."}>
        <div className="space-y-4">
          {currentEmail && <p className="text-sm text-ink-soft" data-testid="profile-current-email">Current: <span className="font-medium text-ink">{currentEmail}</span></p>}
          {emailStage === "idle" ? (
            <>
              <div className="space-y-2"><Label>New email</Label><Input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="new@example.com" data-testid="profile-new-email" /></div>
              <Button onClick={requestEmail} disabled={emailBusy} variant="outline" className="rounded-full" data-testid="email-send-code">{emailBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send verification code</Button>
            </>
          ) : (
            <>
              <div className="space-y-2"><Label>Enter the 6-digit code sent to {newEmail}</Label><Input inputMode="numeric" maxLength={6} value={emailOtp} onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, ""))} placeholder="123456" data-testid="email-otp" /></div>
              <div className="flex gap-2">
                <Button onClick={verifyEmail} disabled={emailBusy} className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid="email-verify">{emailBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Verify &amp; update</Button>
                <Button onClick={() => { setEmailStage("idle"); setEmailOtp(""); }} variant="ghost" className="rounded-full" data-testid="email-cancel">Cancel</Button>
              </div>
            </>
          )}
        </div>
      </Section>
      <Section title={hasPassword ? "Change password" : "Set a password"} description={hasPassword ? "Use at least 6 characters." : "You signed in with Google — set a password to also log in with email."}>
        <div className="space-y-4">
          {hasPassword && <div className="space-y-2"><Label>Current password</Label><Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="pw-current" /></div>}
          <div className="space-y-2"><Label>New password</Label><Input type="password" value={nw} onChange={(e) => setNw(e.target.value)} data-testid="pw-new" /></div>
          <Button onClick={savePassword} disabled={savingPw} variant="outline" className="rounded-full" data-testid="pw-save">{savingPw && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {hasPassword ? "Change password" : "Set password"}</Button>
        </div>
      </Section>
    </>
  );
}


export default function Profile() {
  const { user, setUser, refresh } = useAuth();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(undefined);
  const [switching, setSwitching] = useState(false);
  const [hint, setHint] = useState(null); // { role, needsKyc } shown after a switch

  useEffect(() => { if (user?.role === "restaurant") restaurantApi.mine().then(setRestaurant).catch(() => setRestaurant(null)); }, [user?.role]);

  const [searchParams] = useSearchParams();
  const welcomedRef = useRef(false);
  useEffect(() => {
    if (welcomedRef.current || searchParams.get("welcome") !== "1" || !user) return;
    welcomedRef.current = true;
    const first = (user.name || "there").split(" ")[0];
    const missing = [];
    if (!user.phone) missing.push("phone");
    if (user.email_placeholder) missing.push("email");
    if (!user.default_address) missing.push("address");
    toast.success(missing.length
      ? `Welcome, ${first}! Add your ${missing.join(", ")} below to finish setting up.`
      : `Welcome, ${first}! Your profile is all set.`);
  }, [searchParams, user]);

  const phoneRef = useRef(null);
  const [highlightPhone, setHighlightPhone] = useState(false);
  useEffect(() => {
    if (searchParams.get("verifyPhone") !== "1" || !user) return;
    setHighlightPhone(true);
    const t = setTimeout(() => { phoneRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }); }, 300);
    const t2 = setTimeout(() => setHighlightPhone(false), 3500);
    return () => { clearTimeout(t); clearTimeout(t2); };
  }, [searchParams, user]);

  const switchRole = async (role) => {
    if (role === user.role) return;
    setSwitching(true);
    try {
      const r = await authApi.switchRole(role);
      if (r.token) localStorage.setItem("kt_token", r.token);
      const u = r.user || (await refresh());
      if (u) setUser(u);
      setHint({ role, needsKyc: roleNeedsKyc(u, role) });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const notEnabled = (typeof detail === "string" && /disabl|not.*enabl/i.test(detail));
      if (notEnabled) toast.error("Role switching isn't enabled on the server yet — it will work once the backend update ships.");
      else toast.error(errorMessage(e));
    } finally { setSwitching(false); }
  };

  const dismissHint = () => {
    const h = hint;
    setHint(null);
    if (!h) return;
    if (!h.needsKyc) { navigate(homeForRole(h.role)); return; }
    // Driver -> combined Profile (vehicle/type then KYC). Restaurant -> Restaurant info & KYC.
    navigate(h.role === "driver" ? "/app/profile" : h.role === "restaurant" ? "/app/restaurant/info" : "/app/kyc");
  };

  if (!user) return null;
  const hintData = hint ? ROLE_HINTS[hint.role] : null;
  return (
    <>
      <Dialog open={!!hint} onOpenChange={(o) => { if (!o) dismissHint(); }}>
        <DialogContent className="max-w-md bg-white" data-testid="role-onboarding-hint">
          {hintData && (
            <>
              <DialogHeader>
                <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand">{React.createElement(hintData.icon, { className: "h-6 w-6" })}</div>
                <DialogTitle className="text-xl">{hintData.title}</DialogTitle>
              </DialogHeader>
              <p className="text-sm font-medium text-ink-soft">Here's what you can do next:</p>
              <ul className="space-y-2.5">
                {hintData.items.map((it, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-ink"><Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand" />{it}</li>
                ))}
              </ul>
              <Button onClick={dismissHint} className="mt-2 w-full rounded-full bg-brand text-white hover:bg-brand-600" data-testid="role-hint-continue">{hint.needsKyc ? (hint.role === "driver" ? "Set up your driver profile" : "Complete KYC") : `Go to ${roleLabel(hint.role)} home`}</Button>
            </>
          )}
        </DialogContent>
      </Dialog>
      <PageHeader title="Profile" description="Your account works across the mobile app and this website." />
      <div className="grid gap-6 xl:grid-cols-[340px_1fr]">
        <div className="space-y-6">
          <div className="kt-card p-6 text-center" data-testid="profile-card">
            {user.photo ? <img src={user.photo} alt={user.name} className="mx-auto h-20 w-20 rounded-full object-cover" data-testid="profile-photo" /> : <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-graphite text-2xl font-bold text-white">{user.name?.[0]?.toUpperCase()}</span>}
            <h2 className="mt-4 text-xl font-bold text-ink">{user.name}</h2>
            <p className="text-sm text-ink-soft">{user.email?.endsWith("@phone.local") ? user.phone : user.email}</p>
            <div className="mt-3 flex flex-wrap items-center justify-center gap-2"><span className="rounded-full bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand">{user.role === "rider" ? "Customer" : titleCase(user.role)}</span><StatusBadge status={user.verification_status} /><span className="inline-flex items-center gap-1 text-xs text-ink-soft"><Star className="h-3.5 w-3.5 fill-brand text-brand" />{user.rating}</span></div>
            <p className="mt-4 text-xs text-ink-muted">Member since {formatDate(user.created_at, { timeStyle: undefined })} · via {user.provider}</p>
          </div>
          {user.role === "rider" && <ProfileReminder />}
          {user.role !== "admin" && (
            <Section title="Switch role" description="Use the same account as a customer, driver or restaurant.">
              <div className="grid grid-cols-3 gap-2">
                {ROLES.map((r) => <button key={r.value} disabled={switching} onClick={() => switchRole(r.value)} className={`flex flex-col items-center gap-1.5 rounded-xl border p-3 text-xs font-semibold transition-colors ${user.role === r.value ? "border-brand bg-brand-50 text-brand" : "border-surface-300 text-ink-soft hover:border-brand-200"}`} data-testid={`switch-${r.value}`}><r.icon className="h-5 w-5" />{r.label}{r.value !== "rider" && roleNeedsKyc(user, r.value) && user.role !== r.value && <span className="text-[9px] font-medium text-ink-muted">KYC needed</span>}</button>)}
              </div>
              <p className="mt-3 text-xs text-ink-muted">Driver and Restaurant require KYC verification. Switching to Customer is always instant.</p>
              {switching && <p className="mt-2 inline-flex items-center gap-2 text-xs text-ink-muted"><Repeat className="h-3.5 w-3.5 animate-spin" /> Switching…</p>}
            </Section>
          )}
        </div>
        <div className="space-y-6">
          {["driver", "restaurant"].includes(user.role) && <ProfileProgress />}
          {user.role === "driver" && <DriverReadiness />}
          <AccountSettings user={user} onSaved={(u) => setUser({ ...user, ...u })} />
          {user.role === "driver" && <Section title="Driver type & vehicle" description="Choose how you work, and add your vehicle make, model and number plate."><DriverVehicle user={user} onSaved={(u) => setUser({ ...user, ...u })} /></Section>}
          {user.role === "driver" && user.taxi_scope != null && <Section title="Service area" description="Local = within-city only · Regional = intercity only · Both = either. No extra documents needed."><DriverServiceArea user={user} onSaved={(u) => setUser({ ...user, ...u })} /></Section>}
          {user.role === "driver" && <Section title="KYC documents" description="Upload the documents required for your driver type. Verification is required before you can accept work."><KycStatus embedded /></Section>}
          <div ref={phoneRef} className={highlightPhone ? "rounded-2xl ring-2 ring-brand ring-offset-2 transition-shadow" : ""}><Section title="Phone number" description="Required. Verify your mobile number with a WhatsApp code so we can reach you about rides and orders."><PhoneField user={user} onSaved={(u) => setUser({ ...user, ...u })} /></Section></div>
          <Section title="WhatsApp updates" description="Get ride & order notifications on WhatsApp. Standard WhatsApp messaging applies."><WhatsAppOptIn user={user} onSaved={(u) => setUser({ ...user, ...u })} /></Section>
          {user.role !== "admin" && <Section title="Notification preferences" description="Choose which emails & WhatsApp messages you receive. Everything always shows in the app."><NotificationPrefs user={user} onSaved={(u) => setUser({ ...user, ...u })} /></Section>}
          <Section title="Support" description="Chat with our team on WhatsApp.">
            <WhatsAppChatButton variant="soft" label="Need help? Chat with support" text={`Hi KohimaTaxi, I need help with my account (${user.email || user.phone || user.user_id}). `} testId="profile-whatsapp-help" />
          </Section>
          {["rider", "customer"].includes(user.role) && <Section title="Saved addresses" description="Used for quick checkout."><Addresses /></Section>}
          {["rider", "customer"].includes(user.role) && <Section title="My reports" description="Track misconduct reports you filed and the action taken."><MyReports /></Section>}
          {["rider", "customer"].includes(user.role) && <ReliabilityCard />}
          {["rider", "customer"].includes(user.role) && <CancellationFeesCard />}
          {["rider", "customer"].includes(user.role) && <RefundHistoryCard />}
          {user.role === "restaurant" && (
            <Section title="Restaurant info & KYC" description="Your restaurant profile, contact details and verification documents are managed on their own page.">
              <div className="flex items-center gap-3">
                <Button onClick={() => navigate("/app/restaurant/info")} className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid="profile-restaurant-info-link">Open Restaurant info &amp; KYC</Button>
                {user.verification_status !== "verified" && <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-800" data-testid="restaurant-info-badge">Action needed</span>}
              </div>
            </Section>
          )}
          {user.role !== "admin" && <Section title="Blocked users" description="People you've blocked can't message you, and their reviews are hidden from you."><BlockedUsers /></Section>}
          {user.role !== "admin" && <DeleteAccount />}
        </div>
      </div>
    </>
  );
}
